import "@/App.css";
import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { RequireAuth } from "@/components/RequireAuth";

import PublicLayout from "@/components/layout/PublicLayout";
import Home from "@/pages/public/Home";

// Route-level code splitting: the public home page ships only what it renders; every other screen loads on demand.
// If a chunk fails to load (typically a stale tab after a new deployment), reload once to pick up the new build.
const lazyPage = (loader) => lazy(() => loader().catch((err) => {
  const key = "pg_chunk_reload";
  if (!sessionStorage.getItem(key)) { sessionStorage.setItem(key, "1"); window.location.reload(); return new Promise(() => {}); }
  throw err;
}));
const Catalogue = lazyPage(() => import("@/pages/public/Catalogue"));
const ProductDetail = lazyPage(() => import("@/pages/public/ProductDetail"));
const About = lazyPage(() => import("@/pages/public/About"));
const Quality = lazyPage(() => import("@/pages/public/Quality"));
const CalculatorPage = lazyPage(() => import("@/pages/public/CalculatorPage"));
const Membership = lazyPage(() => import("@/pages/public/Membership"));
const Contact = lazyPage(() => import("@/pages/public/Contact"));
const Legal = lazyPage(() => import("@/pages/public/Legal"));
const Login = lazyPage(() => import("@/pages/auth/Login"));
const PreviewStart = lazyPage(() => import("@/pages/auth/PreviewStart"));
const Activate = lazyPage(() => import("@/pages/auth/Activate"));
const ForgotPassword = lazyPage(() => import("@/pages/auth/ForgotPassword"));
const ResetPassword = lazyPage(() => import("@/pages/auth/ResetPassword"));
const MemberLayout = lazyPage(() => import("@/pages/member/MemberLayout"));
const MemberDashboard = lazyPage(() => import("@/pages/member/MemberDashboard"));
const MemberProducts = lazyPage(() => import("@/pages/member/MemberProducts"));
const MemberOrders = lazyPage(() => import("@/pages/member/MemberOrders"));
const MemberSubscriptions = lazyPage(() => import("@/pages/member/MemberSubscriptions"));
const MemberInvoices = lazyPage(() => import("@/pages/member/MemberInvoices"));
const MemberPurchaseHistory = lazyPage(() => import("@/pages/member/MemberPurchaseHistory"));
const AdminOrders = lazyPage(() => import("@/pages/admin/AdminOrders"));
const AdminBilling = lazyPage(() => import("@/pages/admin/AdminBilling"));
const MemberProfile = lazyPage(() => import("@/pages/member/MemberProfile"));
const MemberCertificates = lazyPage(() => import("@/pages/member/MemberCertificates"));
const MemberSupport = lazyPage(() => import("@/pages/member/MemberSupport"));
const AdminSupport = lazyPage(() => import("@/pages/admin/AdminSupport"));
const AdminMaintenance = lazyPage(() => import("@/pages/admin/AdminMaintenance"));
const AdminLayout = lazyPage(() => import("@/pages/admin/AdminLayout"));
const AdminOverview = lazyPage(() => import("@/pages/admin/AdminOverview"));
const AdminApplications = lazyPage(() => import("@/pages/admin/AdminApplications"));
const AdminMembers = lazyPage(() => import("@/pages/admin/AdminMembers"));
const AdminProducts = lazyPage(() => import("@/pages/admin/AdminProducts"));
const AdminProductEditor = lazyPage(() => import("@/pages/admin/AdminProductEditor"));
const AdminPricing = lazyPage(() => import("@/pages/admin/AdminPricing"));
const AdminOutbox = lazyPage(() => import("@/pages/admin/AdminOutbox"));
const AdminLogs = lazyPage(() => import("@/pages/admin/AdminLogs"));
const AdminLegal = lazyPage(() => import("@/pages/admin/AdminLegal"));
const AdminCalculator = lazyPage(() => import("@/pages/admin/AdminCalculator"));
const AdminPaymentGuide = lazyPage(() => import("@/pages/admin/AdminPaymentGuide"));
const MemberHowToPay = lazyPage(() => import("@/pages/member/MemberHowToPay"));
const AdminOrderEnquiries = lazyPage(() => import("@/pages/admin/AdminOrderEnquiries"));
const AdminSalesControl = lazyPage(() => import("@/pages/admin/AdminSalesControl"));

const RouteFallback = () => (
  <div className="flex min-h-[40vh] items-center justify-center" aria-busy="true" aria-live="polite">
    <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-blue/30 border-t-brand-blue" />
    <span className="sr-only">Loading</span>
  </div>
);

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Suspense fallback={<RouteFallback />}>
          <Routes>
            {/* Public */}
            <Route element={<PublicLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/products" element={<Catalogue />} />
              <Route path="/products/:id" element={<ProductDetail />} />
              <Route path="/about" element={<About />} />
              <Route path="/quality" element={<Quality />} />
              <Route path="/calculator" element={<CalculatorPage />} />
              <Route path="/membership" element={<Membership />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/legal/:slug" element={<Legal />} />
            </Route>

            {/* Auth */}
            <Route path="/login" element={<Login />} />
            <Route path="/admin/login" element={<Login admin />} />
            <Route path="/preview" element={<PreviewStart />} />
            <Route path="/activate" element={<Activate />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* Member */}
            <Route path="/member" element={<RequireAuth role="member"><MemberLayout /></RequireAuth>}>
              <Route index element={<MemberDashboard />} />
              <Route path="products" element={<MemberProducts />} />
              <Route path="orders" element={<MemberOrders />} />
              <Route path="subscriptions" element={<MemberSubscriptions />} />
              <Route path="invoices" element={<MemberInvoices />} />
              <Route path="history" element={<MemberPurchaseHistory />} />
              <Route path="certificates" element={<MemberCertificates />} />
              <Route path="support" element={<MemberSupport />} />
              <Route path="calculator" element={<CalculatorPage embedded />} />
              <Route path="how-to-pay" element={<MemberHowToPay />} />
              <Route path="profile" element={<MemberProfile />} />
            </Route>

            {/* Admin */}
            <Route path="/admin" element={<RequireAuth role="admin"><AdminLayout /></RequireAuth>}>
              <Route index element={<AdminOverview />} />
              <Route path="applications" element={<AdminApplications />} />
              <Route path="members" element={<AdminMembers />} />
              <Route path="products" element={<AdminProducts />} />
              <Route path="products/new" element={<AdminProductEditor />} />
              <Route path="products/:id" element={<AdminProductEditor />} />
              <Route path="enquiries" element={<AdminOrderEnquiries />} />
              <Route path="sales" element={<AdminSalesControl />} />
              <Route path="orders" element={<AdminOrders />} />
              <Route path="billing" element={<AdminBilling />} />
              <Route path="support" element={<AdminSupport />} />
              <Route path="maintenance" element={<AdminMaintenance />} />
              <Route path="pricing" element={<AdminPricing />} />
              <Route path="outbox" element={<AdminOutbox />} />
              <Route path="logs" element={<AdminLogs />} />
              <Route path="legal" element={<AdminLegal />} />
              <Route path="calculator" element={<AdminCalculator />} />
              <Route path="payment-guide" element={<AdminPaymentGuide />} />
            </Route>
          </Routes>
          </Suspense>
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
